import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  FileText,
  Inbox,
  Package,
  Wallet,
} from "lucide-react";

import FinancialGate from "@/components/financial/FinancialGate";
import FinancialShell from "@/components/financial/FinancialShell";
import TrackingTagBadge from "@/components/financial/TrackingTagBadge";
import Footer from "@/components/Footer";
import Navbar from "@/components/Navbar";
import { useLanguage } from "@/context/LanguageContext";
import { useOdoo } from "@/context/OdooContext";
import { formatDate, formatMoney } from "@/utils/odooFormat";
import { formatOdooError } from "@/utils/odooErrors";

const STR = {
  en: {
    brand: "BOB Services LB",
    back: "Back",
    hub_title: "Customer Portal",
    hub_service_orders: "Order status",
    hub_service_orders_sub: "Sales orders and tracking status",
    hub_invoices: "Payments",
    hub_invoices_sub: "Deposits paid and amount still due",
    hub_statement: "Payments",
    hub_statement_sub: "Deposits, payments, and amount due",
    hub_summary: "Statement of Account",
    hub_summary_sub: "Ledger lines and Partner Ledger PDF",
    gate_signin_title: "Sign in required",
    gate_signin_text: "Sign in with your BOB account to view orders, invoices, and your statement.",
    gate_signin_btn: "Sign in",
    gate_link_title: "Link Odoo portal",
    gate_link_text: "Connect your Odoo customer portal login to load orders, invoices, and your statement of account.",
    gate_link_hint: "We could not link automatically with your BOB sign-in. Use your Odoo portal email and password.",
    gate_link_btn: "Link portal account",
    gate_no_match_title: "Customer Portal unavailable",
    gate_no_match_text: "We could not load your BOB Services LB Customer Portal right now. Please try again in a moment.",
    gate_retry_btn: "Try again",
    orders_title: "Order status",
    orders_subtitle: "Sales orders with tracking status",
    orders_empty: "No orders yet.",
    orders_error: "Could not load orders.",
    orders_status: "Status: {state} · Invoice: {invoice}",
    order_detail_title: "Order detail",
    order_detail_not_found: "Order not found.",
    order_detail_error: "Could not load order.",
    invoices_title: "Invoices",
    invoices_subtitle: "Posted customer invoices",
    invoices_empty: "No invoices yet.",
    invoices_error: "Could not load invoices.",
    invoices_due: "Due {amount}",
    invoices_total: "Total {amount}",
    statement_title: "Statement of Account",
    statement_subtitle: "Partner ledger — invoices, payments, and running balance",
    statement_outstanding: "Outstanding",
    statement_error: "Could not load statement.",
    statement_dr_cr: "Dr {debit} · Cr {credit}",
    statement_opening: "Opening balance",
    statement_empty: "No ledger lines in this period.",
    statement_invoiced: "Invoiced",
    statement_paid: "Paid",
    statement_pdf_title: "Partner Ledger PDF",
    statement_pdf_error: "Could not load Partner Ledger PDF.",
    statement_pdf_empty: "No Partner Ledger PDF available.",
    statement_pdf_open: "Open PDF in new tab",
    statement_lines_title: "Ledger activity",
    dashboard_title: "Dashboard",
    dashboard_subtitle: "Outstanding balance and overview",
    dashboard_outstanding: "Outstanding",
    dashboard_orders: "Order status",
    dashboard_invoices: "Invoices",
    dashboard_payments: "Payments",
    dashboard_error: "Could not load summary.",
    link_title: "Link portal",
    link_subtitle: "Use your Odoo customer portal login",
    link_help: "Portal users only. Admin Odoo accounts cannot be used here. Your API key is stored securely in this browser.",
    link_email: "Email",
    link_password: "Portal password",
    link_submit: "Link portal account",
    link_linking: "Linking...",
    link_success: "Portal linked successfully.",
    error_forbidden: "Portal access only. Admin Odoo accounts cannot be linked here.",
    error_unauthorized: "Session expired. Link your portal account again.",
    error_network: "Network error. Check your connection and try again.",
    error_generic: "Something went wrong. Please try again.",
    hub_payments: "Payments",
    hub_payments_sub: "Deposits paid and amount still due",
    hub_outstanding: "Amount to pay",
    hub_outstanding_hint: "Total outstanding balance on your account",
    hub_deposit_not_deducted: "Deposit",
    payments_title: "Payments",
    payments_subtitle: "Deposits on invoices and amount still due",
    payments_empty: "No invoices or payments yet.",
    payments_error: "Could not load payments.",
    payments_outstanding: "Amount to pay",
    payments_deposit_not_deducted: "Deposit",
    payments_deposit: "Deposit paid {amount}",
    payments_due: "Still due {amount}",
    payments_total: "Invoice total {amount}",
    payments_list_title: "Payment history",
    payments_invoices_title: "Invoices & deposits",
    refresh: "Refresh",
  },
  fr: {
    brand: "BOB Services LB",
    back: "Retour",
    hub_title: "Portail client",
    hub_service_orders: "Statut des commandes",
    hub_service_orders_sub: "Commandes et suivi",
    hub_invoices: "Factures",
    hub_invoices_sub: "Factures comptabilisées et statut de paiement",
    hub_statement: "Relevé",
    hub_statement_sub: "Solde impayé et grand livre",
    hub_summary: "Relevé de compte",
    hub_summary_sub: "PDF du grand livre partenaire — factures, paiements et solde",
    gate_signin_title: "Connexion requise",
    gate_signin_text: "Connectez-vous avec votre compte BOB pour voir commandes, factures et relevé.",
    gate_signin_btn: "Se connecter",
    gate_link_title: "Lier le portail Odoo",
    gate_link_text: "Connectez votre compte portail client Odoo pour charger commandes, factures et relevé.",
    gate_link_hint: "La liaison automatique a échoué. Utilisez l'e-mail et le mot de passe du portail Odoo.",
    gate_link_btn: "Lier le compte portail",
    gate_no_match_title: "Portail client indisponible",
    gate_no_match_text: "Impossible de charger le portail client BOB Services LB pour le moment. Réessayez dans un instant.",
    gate_retry_btn: "Réessayer",
    orders_title: "Statut des commandes",
    orders_subtitle: "Commandes avec statut de suivi",
    orders_empty: "Aucune commande pour le moment.",
    orders_error: "Impossible de charger les commandes.",
    orders_status: "Statut : {state} · Facture : {invoice}",
    order_detail_title: "Détail de commande",
    order_detail_not_found: "Commande introuvable.",
    order_detail_error: "Impossible de charger la commande.",
    invoices_title: "Factures",
    invoices_subtitle: "Factures client comptabilisées",
    invoices_empty: "Aucune facture pour le moment.",
    invoices_error: "Impossible de charger les factures.",
    invoices_due: "Dû {amount}",
    invoices_total: "Total {amount}",
    statement_title: "Relevé de compte",
    statement_subtitle: "Grand livre partenaire — factures, paiements et solde",
    statement_outstanding: "Solde impayé",
    statement_error: "Impossible de charger le relevé.",
    statement_dr_cr: "Débit {debit} · Crédit {credit}",
    statement_opening: "Solde d'ouverture",
    statement_empty: "Aucune écriture sur cette période.",
    statement_invoiced: "Facturé",
    statement_paid: "Payé",
    statement_pdf_title: "PDF du grand livre",
    statement_pdf_error: "Impossible de charger le PDF du grand livre.",
    statement_pdf_empty: "Aucun PDF de grand livre disponible.",
    statement_pdf_open: "Ouvrir le PDF",
    statement_lines_title: "Activité du grand livre",
    dashboard_title: "Tableau de bord",
    dashboard_subtitle: "Solde impayé et aperçu",
    dashboard_outstanding: "Solde impayé",
    dashboard_orders: "Statut des commandes",
    dashboard_invoices: "Factures",
    dashboard_payments: "Paiements",
    dashboard_error: "Impossible de charger le résumé.",
    link_title: "Lier le portail",
    link_subtitle: "Utilisez votre connexion portail client Odoo",
    link_help: "Utilisateurs portail uniquement. Les comptes admin Odoo ne peuvent pas être utilisés ici.",
    link_email: "E-mail",
    link_password: "Mot de passe portail",
    link_submit: "Lier le compte portail",
    link_linking: "Liaison...",
    link_success: "Portail lié avec succès.",
    error_forbidden: "Accès portail uniquement.",
    error_unauthorized: "Session expirée. Reliez votre compte portail.",
    error_network: "Erreur réseau. Vérifiez votre connexion.",
    error_generic: "Une erreur est survenue.",
    hub_payments: "Paiements",
    hub_payments_sub: "Acomptes et montant dû",
    hub_outstanding: "Montant à payer",
    hub_outstanding_hint: "Solde total impayé sur votre compte",
    hub_deposit_not_deducted: "Acompte",
    payments_title: "Paiements",
    payments_subtitle: "Acomptes et montant dû",
    payments_empty: "Aucune facture ou paiement pour le moment.",
    payments_error: "Impossible de charger les paiements.",
    payments_outstanding: "Montant à payer",
    payments_deposit_not_deducted: "Acompte",
    payments_deposit: "Acompte payé {amount}",
    payments_due: "Reste dû {amount}",
    payments_total: "Total facture {amount}",
    payments_list_title: "Historique des paiements",
    payments_invoices_title: "Factures et acomptes",
    refresh: "Actualiser",
  },
  ar: {
    brand: "BOB Services LB",
    back: "رجوع",
    hub_title: "بوابة العميل",
    hub_service_orders: "حالة الطلب",
    hub_service_orders_sub: "طلبات المبيعات وحالة التتبع",
    hub_invoices: "الفواتير",
    hub_invoices_sub: "الفواتير المرحّلة وحالة الدفع",
    hub_statement: "كشف الحساب",
    hub_statement_sub: "الرصيد المستحق وسجل الحركات",
    hub_summary: "كشف الحساب",
    hub_summary_sub: "PDF دفتر الأستاذ — الفواتير والمدفوعات والرصيد",
    gate_signin_title: "يلزم تسجيل الدخول",
    gate_signin_text: "سجّل الدخول بحساب BOB لعرض الطلبات والفواتير وكشف الحساب.",
    gate_signin_btn: "تسجيل الدخول",
    gate_link_title: "ربط بوابة Odoo",
    gate_link_text: "اربط حساب بوابة العميل في Odoo لتحميل الطلبات والفواتير وكشف الحساب.",
    gate_link_hint: "تعذّر الربط تلقائياً. استخدم بريد وكلمة مرور بوابة Odoo.",
    gate_link_btn: "ربط حساب البوابة",
    gate_no_match_title: "بوابة العميل غير متاحة",
    gate_no_match_text: "تعذّر تحميل بوابة عملاء BOB Services LB حالياً. يرجى المحاولة بعد لحظات.",
    gate_retry_btn: "إعادة المحاولة",
    orders_title: "حالة الطلب",
    orders_subtitle: "طلبات المبيعات مع حالة التتبع",
    orders_empty: "لا توجد طلبات بعد.",
    orders_error: "تعذّر تحميل الطلبات.",
    orders_status: "الحالة: {state} · الفاتورة: {invoice}",
    order_detail_title: "تفاصيل الطلب",
    order_detail_not_found: "الطلب غير موجود.",
    order_detail_error: "تعذّر تحميل الطلب.",
    invoices_title: "الفواتير",
    invoices_subtitle: "فواتير العميل المرحّلة",
    invoices_empty: "لا توجد فواتير بعد.",
    invoices_error: "تعذّر تحميل الفواتير.",
    invoices_due: "مستحق {amount}",
    invoices_total: "الإجمالي {amount}",
    statement_title: "كشف الحساب",
    statement_subtitle: "دفتر الأستاذ — فواتير ومدفوعات ورصيد جارٍ",
    statement_outstanding: "الرصيد المستحق",
    statement_error: "تعذّر تحميل كشف الحساب.",
    statement_dr_cr: "مدين {debit} · دائن {credit}",
    statement_opening: "الرصيد الافتتاحي",
    statement_empty: "لا توجد حركات في هذه الفترة.",
    statement_invoiced: "المفوتر",
    statement_paid: "المدفوع",
    statement_pdf_title: "ملف PDF لدفتر الأستاذ",
    statement_pdf_error: "تعذّر تحميل ملف PDF لدفتر الأستاذ.",
    statement_pdf_empty: "لا يتوفر ملف PDF لدفتر الأستاذ.",
    statement_pdf_open: "فتح PDF في تبويب جديد",
    statement_lines_title: "حركات دفتر الأستاذ",
    dashboard_title: "لوحة التحكم",
    dashboard_subtitle: "الرصيد المستحق ونظرة عامة",
    dashboard_outstanding: "الرصيد المستحق",
    dashboard_orders: "حالة الطلب",
    dashboard_invoices: "الفواتير",
    dashboard_payments: "المدفوعات",
    dashboard_error: "تعذّر تحميل الملخص.",
    link_title: "ربط البوابة",
    link_subtitle: "استخدم بيانات دخول بوابة العميل في Odoo",
    link_help: "لمستخدمي البوابة فقط. لا يمكن استخدام حسابات Odoo الإدارية هنا.",
    link_email: "البريد الإلكتروني",
    link_password: "كلمة مرور البوابة",
    link_submit: "ربط حساب البوابة",
    link_linking: "جارٍ الربط...",
    link_success: "تم ربط البوابة بنجاح.",
    error_forbidden: "للمستخدمين عبر البوابة فقط.",
    error_unauthorized: "انتهت الجلسة. أعد ربط حساب البوابة.",
    error_network: "خطأ في الشبكة.",
    error_generic: "حدث خطأ ما.",
    hub_payments: "المدفوعات",
    hub_payments_sub: "التأمينات والمبلغ المتبقي",
    hub_outstanding: "المبلغ المطلوب دفعه",
    hub_outstanding_hint: "إجمالي الرصيد المستحق على حسابك",
    hub_deposit_not_deducted: "التأمين",
    payments_title: "المدفوعات",
    payments_subtitle: "التأمينات على الفواتير والمبلغ المتبقي",
    payments_empty: "لا توجد فواتير أو مدفوعات بعد.",
    payments_error: "تعذّر تحميل المدفوعات.",
    payments_outstanding: "المبلغ المطلوب دفعه",
    payments_deposit_not_deducted: "التأمين",
    payments_deposit: "التأمين المدفوع {amount}",
    payments_due: "المتبقي {amount}",
    payments_total: "إجمالي الفاتورة {amount}",
    payments_list_title: "سجل المدفوعات",
    payments_invoices_title: "الفواتير والتأمينات",
    refresh: "تحديث",
  },
};

function useT() {
  const { lang } = useLanguage();
  return STR[lang] || STR.en;
}

function fmt(template, vars = {}) {
  return Object.entries(vars).reduce(
    (s, [k, v]) => s.split(`{${k}}`).join(String(v)),
    template,
  );
}

function useOdooErrorFormatter(t) {
  return useCallback(
    (error) =>
      formatOdooError(error, {
        forbidden: t.error_forbidden,
        unauthorized: t.error_unauthorized,
        network: t.error_network,
        generic: t.error_generic,
      }),
    [t],
  );
}

const HUB_MENU = [
  { href: "/financial/orders", icon: Package, titleKey: "hub_service_orders", subKey: "hub_service_orders_sub" },
  { href: "/financial/payments", icon: Wallet, titleKey: "hub_payments", subKey: "hub_payments_sub" },
  { href: "/financial/statement", icon: FileText, titleKey: "hub_summary", subKey: "hub_summary_sub" },
];

export function FinancialHubPage() {
  const t = useT();
  const { lang } = useLanguage();
  const { client, partnerName, linked } = useOdoo();
  const formatError = useOdooErrorFormatter(t);
  const align = lang === "ar" ? "text-right" : "text-left";
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!client || !linked) {
        setSummary(null);
        return;
      }
      setLoadingSummary(true);
      try {
        const data = await client.getDashboardSummary();
        if (!cancelled) setSummary(data);
      } catch (e) {
        if (!cancelled) setSummary(null);
        formatError(e);
      } finally {
        if (!cancelled) setLoadingSummary(false);
      }
    })();
    return () => { cancelled = true; };
  }, [client, linked, formatError]);

  const currency = summary?.company_currency || "USD";

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-[#f7f7f9] pt-24 pb-16 dark:bg-[#080808]">
        <div className="mx-auto max-w-3xl px-4 sm:px-6" data-testid="financial-hub">
          <div className={`mb-8 ${align}`}>
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#F37021]">{t.brand}</p>
            <h1 className="mt-2 text-3xl font-bold text-[#0a1628] dark:text-white">{t.hub_title}</h1>
            {linked && partnerName ? (
              <p className="mt-2 text-sm text-[#667083] dark:text-[#BAC4D4]">{partnerName}</p>
            ) : null}
          </div>
          <FinancialGate t={t}>
            {linked ? (
              <div className="mb-5 rounded-[24px] border border-[#F37021]/20 bg-[#F37021]/8 p-6" data-testid="financial-hub-outstanding">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-[#667083]">{t.hub_outstanding}</p>
                    <p className="mt-2 text-3xl font-bold text-[#0a1628] dark:text-white">
                      {loadingSummary ? "…" : formatMoney(summary?.outstanding_company_currency ?? 0, currency)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-[#667083]">{t.hub_deposit_not_deducted}</p>
                    <p className="mt-2 text-3xl font-bold text-emerald-700 dark:text-emerald-400">
                      {loadingSummary ? "…" : formatMoney(summary?.deposit ?? summary?.deposit_not_deducted ?? 0, currency)}
                    </p>
                  </div>
                </div>
              </div>
            ) : null}
            <div className="space-y-3">
              {HUB_MENU.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    data-testid={`financial-hub-link-${item.href.split("/").pop()}`}
                    className="flex items-center gap-4 rounded-[24px] border border-black/8 bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-[#F37021]/25 dark:border-white/10 dark:bg-[#121212]"
                  >
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#F37021]/10 text-[#F37021]">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className={`flex-1 ${align}`}>
                      <p className="font-bold text-[#0a1628] dark:text-white">{t[item.titleKey]}</p>
                      <p className="mt-1 text-sm text-[#667083] dark:text-[#BAC4D4]">{t[item.subKey]}</p>
                    </div>
                    <ArrowRight className={`h-4 w-4 text-[#8FA1B8] ${lang === "ar" ? "rotate-180" : ""}`} />
                  </Link>
                );
              })}
            </div>
          </FinancialGate>
        </div>
      </main>
      <Footer />
    </>
  );
}

export function FinancialOrdersPage() {
  const t = useT();
  const { client } = useOdoo();
  const formatError = useOdooErrorFormatter(t);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!client) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await client.getSalesOrders({ page: 1, limit: 40 });
      setOrders(res.sales_orders || []);
    } catch (e) {
      setError(formatError(e) || t.orders_error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [client, formatError, t]);

  useEffect(() => { load(); }, [load]);

  return (
    <>
      <Navbar />
      <FinancialShell title={t.orders_title} subtitle={t.orders_subtitle} loading={Boolean(client) && loading} t={t}>
        <FinancialGate t={t}>
          {error ? <p className="font-semibold text-[#0a1628] dark:text-white">{error}</p> : null}
          {!error && orders.length === 0 ? (
            <div className="rounded-[24px] border border-black/8 bg-white p-10 text-center dark:border-white/10 dark:bg-[#121212]">
              <Inbox className="mx-auto h-8 w-8 text-[#8FA1B8]" />
              <p className="mt-3 text-sm text-[#667083]">{t.orders_empty}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map((order) => (
                <Link
                  key={order.id}
                  to={`/financial/orders/${order.id}`}
                  className="block rounded-[24px] border border-black/8 bg-white p-5 hover:border-[#F37021]/25 dark:border-white/10 dark:bg-[#121212]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-bold text-[#0a1628] dark:text-white" dir="ltr">{order.name || `#${order.id}`}</p>
                      <p className="mt-1 text-sm text-[#667083]">
                        {formatDate(order.date_order)} · {formatMoney(order.amount_total, order.currency)}
                      </p>
                    </div>
                    {order.tracking_tag ? (
                      <TrackingTagBadge name={order.tracking_tag.name} color={order.tracking_tag.color} />
                    ) : null}
                  </div>
                  <p className="mt-2 text-xs font-semibold text-[#667083]">
                    {fmt(t.orders_status, { state: order.state, invoice: order.invoice_status })}
                  </p>
                </Link>
              ))}
            </div>
          )}
        </FinancialGate>
      </FinancialShell>
      <Footer />
    </>
  );
}

export function FinancialOrderDetailPage() {
  const { id } = useParams();
  const t = useT();
  const { client } = useOdoo();
  const formatError = useOdooErrorFormatter(t);
  const [pdfUrl, setPdfUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!client || !id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const blob = await client.getSalesOrderPdfBlob(Number(id));
      setPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
    } catch (e) {
      setError(formatError(e) || t.order_detail_error);
      setPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return "";
      });
    } finally {
      setLoading(false);
    }
  }, [client, formatError, id, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
  }, [pdfUrl]);

  return (
    <>
      <Navbar />
      <FinancialShell title={t.order_detail_title} loading={Boolean(client) && loading} t={t}>
        <FinancialGate t={t}>
          {error || !pdfUrl ? (
            <p className="text-[#0a1628] dark:text-white">{error || t.order_detail_not_found}</p>
          ) : (
            <div className="-mx-1 overflow-hidden rounded-[16px] border border-black/8 bg-white dark:border-white/10 dark:bg-[#121212]">
              <iframe
                title={t.order_detail_title}
                src={pdfUrl}
                className="h-[min(78vh,920px)] w-full border-0 bg-white"
              />
            </div>
          )}
        </FinancialGate>
      </FinancialShell>
      <Footer />
    </>
  );
}

export function FinancialInvoicesPage() {
  return <Navigate to="/financial/payments" replace />;
}

export function FinancialStatementPage() {
  const t = useT();
  const { client } = useOdoo();
  const formatError = useOdooErrorFormatter(t);
  const [summary, setSummary] = useState(null);
  const [lines, setLines] = useState([]);
  const [meta, setMeta] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfError, setPdfError] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!client) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    setPdfError("");
    try {
      const [summaryRes, linesRes] = await Promise.all([
        client.getSummary(),
        client.getLines({ page: 1, limit: 50 }),
      ]);
      setSummary(summaryRes);
      setLines(linesRes.lines || []);
      setMeta(linesRes);
    } catch (e) {
      setError(formatError(e) || t.statement_error);
      setSummary(null);
      setLines([]);
      setMeta(null);
    }

    try {
      const blob = await client.getPartnerLedgerPdfBlob();
      setPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
    } catch (e) {
      setPdfError(formatError(e) || t.statement_pdf_error);
      setPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return "";
      });
    } finally {
      setLoading(false);
    }
  }, [client, formatError, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
  }, [pdfUrl]);

  const currency = meta?.company_currency || summary?.company_currency || "USD";

  const pdfSection = (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-bold uppercase tracking-wider text-[#667083]">{t.statement_pdf_title}</h2>
        {pdfUrl ? (
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-semibold text-[#F37021] hover:underline"
          >
            {t.statement_pdf_open}
          </a>
        ) : null}
      </div>
      {pdfError || !pdfUrl ? (
        <p className="text-[#667083]">{pdfError || (loading ? "…" : t.statement_pdf_empty)}</p>
      ) : (
        <div className="-mx-1 overflow-hidden rounded-[16px] border border-black/8 bg-white dark:border-white/10 dark:bg-[#121212]">
          <iframe
            title={t.statement_pdf_title}
            src={pdfUrl}
            className="h-[min(52vh,640px)] w-full border-0 bg-white"
          />
        </div>
      )}
    </div>
  );

  return (
    <>
      <Navbar />
      <FinancialShell title={t.statement_title} subtitle={t.statement_subtitle} loading={Boolean(client) && loading} t={t}>
        <FinancialGate t={t}>
          {error ? (
            <p className="font-semibold text-[#0a1628] dark:text-white">{error}</p>
          ) : (
            <div className="space-y-4">
              <div className="rounded-[24px] border border-[#F37021]/20 bg-[#F37021]/8 p-6">
                <p className="text-xs font-bold uppercase tracking-wider text-[#667083]">{t.statement_outstanding}</p>
                <p className="mt-2 text-3xl font-bold text-[#0a1628] dark:text-white">
                  {formatMoney(summary?.outstanding_company_currency ?? 0, currency)}
                </p>
                {summary?.partner?.name ? (
                  <p className="mt-2 text-sm text-[#667083]">{summary.partner.name}</p>
                ) : null}
                {meta ? (
                  <div className="mt-4 grid gap-3 sm:grid-cols-3">
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-[#667083]">{t.statement_opening}</p>
                      <p className="mt-1 text-lg font-bold text-[#0a1628] dark:text-white">{formatMoney(meta.opening_balance, currency)}</p>
                    </div>
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-[#667083]">{t.statement_invoiced}</p>
                      <p className="mt-1 text-lg font-bold text-[#0a1628] dark:text-white">{formatMoney(meta.total_invoiced, currency)}</p>
                    </div>
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-[#667083]">{t.statement_paid}</p>
                      <p className="mt-1 text-lg font-bold text-[#0a1628] dark:text-white">{formatMoney(meta.total_paid, currency)}</p>
                    </div>
                  </div>
                ) : null}
              </div>

              {pdfSection}

              <div className="space-y-3">
                <h2 className="text-sm font-bold uppercase tracking-wider text-[#667083]">{t.statement_lines_title}</h2>
                {!lines.length ? (
                  <p className="text-[#667083]">{t.statement_empty}</p>
                ) : (
                  lines.map((line) => (
                    <div key={line.id} className="rounded-[24px] border border-black/8 bg-white p-5 dark:border-white/10 dark:bg-[#121212]">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-[#0a1628] dark:text-white" dir="ltr">{line.reference || line.description || "—"}</p>
                          <p className="mt-1 text-sm text-[#667083]">
                            {formatDate(line.date)}
                            {line.description && line.reference ? ` · ${line.description}` : ""}
                          </p>
                          <p className="mt-1 text-xs text-[#667083]">
                            {fmt(t.statement_dr_cr, {
                              debit: formatMoney(line.debit, currency),
                              credit: formatMoney(line.credit, currency),
                            })}
                          </p>
                        </div>
                        <p className="shrink-0 text-right text-base font-bold text-[#0a1628] dark:text-white" dir="ltr">
                          {formatMoney(line.balance, currency)}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </FinancialGate>
      </FinancialShell>
      <Footer />
    </>
  );
}

export function FinancialPaymentsPage() {
  const t = useT();
  const { client } = useOdoo();
  const formatError = useOdooErrorFormatter(t);
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!client) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [summaryRes, invoicesRes, paymentsRes] = await Promise.all([
        client.getDashboardSummary(),
        client.getInvoices({ page: 1, limit: 40 }),
        client.getPayments({ page: 1, limit: 40 }),
      ]);
      setSummary(summaryRes);
      setInvoices(invoicesRes.invoices || []);
      setPayments(paymentsRes.payments || []);
    } catch (e) {
      setError(formatError(e) || t.payments_error);
      setSummary(null);
      setInvoices([]);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [client, formatError, t]);

  useEffect(() => { load(); }, [load]);

  const currency = summary?.company_currency || "USD";

  return (
    <>
      <Navbar />
      <FinancialShell title={t.payments_title} subtitle={t.payments_subtitle} loading={Boolean(client) && loading} t={t}>
        <FinancialGate t={t}>
          {error ? <p className="font-semibold">{error}</p> : null}
          {!error ? (
            <div className="space-y-5">
              <div className="rounded-[24px] border border-[#F37021]/20 bg-[#F37021]/8 p-6" data-testid="payments-outstanding">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-[#667083]">{t.payments_outstanding}</p>
                    <p className="mt-2 text-3xl font-bold text-[#0a1628] dark:text-white">
                      {formatMoney(summary?.outstanding_company_currency ?? 0, currency)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-[#667083]">{t.payments_deposit_not_deducted}</p>
                    <p className="mt-2 text-3xl font-bold text-emerald-700 dark:text-emerald-400">
                      {formatMoney(summary?.deposit ?? summary?.deposit_not_deducted ?? 0, currency)}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="mb-3 text-sm font-bold uppercase tracking-wider text-[#667083]">{t.payments_invoices_title}</h2>
                {invoices.length === 0 ? (
                  <div className="rounded-[24px] border border-black/8 bg-white p-8 text-center dark:border-white/10 dark:bg-[#121212]">
                    <Inbox className="mx-auto h-8 w-8 text-[#8FA1B8]" />
                    <p className="mt-3 text-sm text-[#667083]">{t.payments_empty}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {invoices.map((invoice) => {
                      const deposit = Math.max(0, Number(invoice.amount_total || 0) - Number(invoice.amount_residual || 0));
                      return (
                        <div key={invoice.id} className="rounded-[24px] border border-black/8 bg-white p-5 dark:border-white/10 dark:bg-[#121212]" data-testid="payments-invoice-card">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              {invoice.sales_order || (invoice.sales_orders && invoice.sales_orders[0]) || invoice.invoice_origin ? (
                                <p className="text-xs font-bold uppercase tracking-wider text-[#667083]" dir="ltr">
                                  {invoice.sales_order || invoice.sales_orders?.[0] || invoice.invoice_origin}
                                </p>
                              ) : null}
                              <p className="font-bold text-[#0a1628] dark:text-white" dir="ltr">{invoice.name}</p>
                              <p className="mt-1 text-sm text-[#667083]">{formatDate(invoice.invoice_date)}</p>
                            </div>
                            <span className="rounded-full bg-[#F37021]/10 px-3 py-1 text-[10px] font-bold uppercase text-[#F37021]">
                              {invoice.payment_state}
                            </span>
                          </div>
                          <div className="mt-3 grid gap-2 text-sm sm:grid-cols-3">
                            <p className="font-semibold text-[#0a1628] dark:text-white">
                              {fmt(t.payments_total, { amount: formatMoney(invoice.amount_total, invoice.currency) })}
                            </p>
                            <p className="font-semibold text-emerald-700 dark:text-emerald-400">
                              {fmt(t.payments_deposit, { amount: formatMoney(deposit, invoice.currency) })}
                            </p>
                            <p className="font-semibold text-[#B45309]">
                              {fmt(t.payments_due, { amount: formatMoney(invoice.amount_residual, invoice.currency) })}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {payments.length > 0 ? (
                <div>
                  <h2 className="mb-3 text-sm font-bold uppercase tracking-wider text-[#667083]">{t.payments_list_title}</h2>
                  <div className="space-y-3">
                    {payments.map((payment) => (
                      <div key={payment.id} className="rounded-[24px] border border-black/8 bg-white p-5 dark:border-white/10 dark:bg-[#121212]">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-bold text-[#0a1628] dark:text-white" dir="ltr">{payment.name}</p>
                            <p className="mt-1 text-sm text-[#667083]">{formatDate(payment.date)}</p>
                          </div>
                          <p className="font-bold text-[#0a1628] dark:text-white">
                            {formatMoney(payment.amount, payment.currency)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </FinancialGate>
      </FinancialShell>
      <Footer />
    </>
  );
}

export function FinancialDashboardPage() {
  return <Navigate to="/financial/statement" replace />;
}

export function FinancialLinkPage() {
  return <Navigate to="/financial" replace />;
}
